import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueries, useQueryClient } from '@tanstack/react-query'

import { Table } from '@aws-northstar/ui'
import { Alert, Button, StatusIndicator } from '@cloudscape-design/components'

import LoadingSpinner from '../../components/LoadingSpinner'

import type { gcpProject } from '../../types/gcpProject'
import api from '../../api'
import useGcpProjects from '../../hooks/query/useGcpProject'
import {
	getSortedGcpProjectTableItems,
	type GcpProjectTableItem,
	type GcpProjectTagsData
} from '../../helper/gcpProjectList'

import { usegcpProjectCreationFormStore } from '../../stores/gcpProjects/newgcpProjectCreationFormStore'
import { useGenericTagsManagementFormStore } from '../../stores/tags/genericTagsManagementFormStore'
import { getApprovedGcpProjectsWithTags } from '../../hooks/query/useApprovedGcpProjectsWithTags'

const GcpProjects: FC = () => {
	const navigate = useNavigate()
	const queryClient = useQueryClient()


	const { data: gcpProjects, isLoading, isError } = useGcpProjects()
	const gcpProjectCreationFormStore = usegcpProjectCreationFormStore()
	const tagCreationFormStore = useGenericTagsManagementFormStore()
	const tagQueries = useQueries({
		queries: (gcpProjects || []).map((project) => ({
			queryKey: ['tags', project.tagsID],
			queryFn: () => api.tags.getTagsById(project.tagsID as string),
			enabled: Boolean(project.tagsID)
		}))
	})

	const prefetchApprovedGcpProjects = () => {
		queryClient.prefetchQuery({
			queryKey: ['approvedGcpProjects', 'withTags'],
			queryFn: getApprovedGcpProjectsWithTags,
			staleTime: 5 * 60 * 1000
		})
	}

	const isTagLoading = tagQueries.some((query) => query.isLoading)

	if (isLoading) return <LoadingSpinner />

	if (isError) {
		return (
			<Alert
				type="error"
				header="Unable to load GCP Projects"
				statusIconAriaLabel="Error"
			>
				The GCP Projects list could not be loaded right now.
			</Alert>
		)
	}
	
	const gcpProjectItems = getSortedGcpProjectTableItems(
		gcpProjects,
		tagQueries.map((query) => query.data as GcpProjectTagsData | undefined)
	)

	const columnDefinitions = [
		{
			id: 'gcpProjectID',
			width: 260,
			header: 'GCP Project Name',
			cell: (data: GcpProjectTableItem) => (
				<Button variant="link" onClick={() => navigate(data.gcpProjectID)}>
					{data.gcpProjectName}
				</Button>
			),
			sortingField: 'gcpProjectName'
		},
		{
			id: 'platformName',
			width: 100,
			header: 'Platform Name',
			cell: (data: GcpProjectTableItem) => data.platformName || '',
			sortingField: 'platformName'
		},
		{
			id: 'type',
			width: 100,
			header: 'Environment',
			cell: (data: gcpProject) => data.type || '',
			sortingField: 'type'
		},
		{
			id: 'requestor',
			width: 100,
			header: 'Requested by',
			cell: (data: gcpProject) => data.requestorID || '',
			sortingField: 'requestorID'
		},
		{
			id: 'time',
			width: 100,
			header: 'Date Created',
			sortingField: 'creationTime',
			cell: (data: gcpProject) => {
				if (data.creationTime) {
					const date = new Date(data.creationTime);
					const localDate = date.toLocaleString("en-US", 
						{ 
						timeZone: "Asia/Singapore",
						weekday: "short",
						month: "numeric",
						day: "numeric",
						year: "numeric",
						hour: "numeric",
						minute: "numeric"
						}
					)
				
					return localDate
				}
				return '';
    		}
		},
		{
			id: 'status',
			width: 100,
			header: 'Status',
			cell: (data: gcpProject) => {
				const status = data.status
				switch (status) {
					case 'PENDING_APPROVAL':
						return (
							<StatusIndicator type="in-progress" colorOverride="blue">
								Pending Approval
							</StatusIndicator>
						)
					case 'APPROVED':
						return <StatusIndicator type="success">Approved</StatusIndicator>
					case 'REJECTED':
						return <StatusIndicator type="error">Rejected</StatusIndicator>
					default:
						return null
				}
			},
			sortingField: 'status'
		}
	]

	const tableActions = (
		<Button
			variant="primary"
			onClick={() => {
				gcpProjectCreationFormStore.actions.clearFormValues()
				gcpProjectCreationFormStore.actions.setCurrentStep(0)
				tagCreationFormStore.actions.clearFormValues()
				prefetchApprovedGcpProjects()
				navigate('/gcpProjects/new')
			}}
		>
			New GCP Project Request
		</Button>
	)

	return (
		<Table
			header="Projects"
			columnDefinitions={columnDefinitions}
			items={isTagLoading ? [] : gcpProjectItems}
			actions={tableActions}
			loading={isTagLoading}
			disableRowSelect
		/>
	)
}

export default GcpProjects
